//* Number of seconds before actual expiry at which the access token is treated
//* as "stale" and refreshed proactively — covers request latency and minor clock skew.
const TOKEN_EXPIRY_BUFFER_SECONDS = 30;

interface IJwtPayload {
  exp?: number;
}

//* Decode a base64url-encoded JWT segment into a UTF-8 string.
//* Uses the portable atob + decodeURIComponent idiom (works in browsers and jsdom)
//* so no TextDecoder/TextEncoder polyfill is required.
const base64UrlDecode = (segment: string): string => {
  const base64 = segment.replace(/-/g, '+').replace(/_/g, '/');
  const padding = (4 - (base64.length % 4)) % 4;
  const padded = base64.padEnd(base64.length + padding, '=');

  return decodeURIComponent(
    atob(padded)
      .split('')
      .map(char => `%${`00${char.charCodeAt(0).toString(16)}`.slice(-2)}`)
      .join(''),
  );
};

//* Read the `exp` claim (in milliseconds) from a JWT access token.
//* Returns null if the token is missing, malformed, or has no numeric `exp`.
export const getTokenExpiryMs = (token: string): number | null => {
  try {
    const payloadSegment = token.split('.')[1];

    if (!payloadSegment) {
      return null;
    }

    const payload = JSON.parse(base64UrlDecode(payloadSegment)) as IJwtPayload;

    return typeof payload.exp === 'number' ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
};

//* True when the token is already expired or within the refresh buffer of expiring.
//* Returns false for tokens we can't decode so the reactive 401 handler stays the fallback.
export const isAccessTokenExpiringSoon = (token: string): boolean => {
  const expiryMs = getTokenExpiryMs(token);

  if (expiryMs === null) {
    return false;
  }

  return expiryMs - Date.now() <= TOKEN_EXPIRY_BUFFER_SECONDS * 1000;
};
